// frontend/src/services/reservationService.js
// Contains functions for interacting with the /reservations endpoints

import axiosInstance from './axiosConfig'; // Ensure this is your configured Axios instance

// --- Public Functions ---
export const getPublicCalendarData = async (year, month) => {
    try {
        const response = await axiosInstance.get(`/reservations/public-calendar?year=${year}&month=${month}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching public calendar data:", error.response?.data || error.message);
        throw error; // Re-throw to be caught by the component
    }
};

// --- Member Functions ---

/**
 * Sends a reservation request to the backend.
 * @param {object} reservationData - Object containing reservation details including startTime, endTime, eventName, eventDescription, etc.
 * @returns {Promise<object>} Backend response (likely the new reservation)
 */
export const createReservation = async (reservationData) => {
    try {
        const response = await axiosInstance.post('/reservations', reservationData);
        return response.data;
    } catch (error) {
        console.error("API Error creating reservation (/reservations):", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to create reservation');
    }
};

/**
 * Fetches reservations belonging to the currently logged-in user.
 * @returns {Promise<Array>} Array of reservation objects.
 */
export const getMyReservations = async () => {
    try {
        const response = await axiosInstance.get('/reservations/my-reservations');
        return response.data || [];
    } catch (error) {
        console.error("API Error fetching user reservations (/reservations/my-reservations):", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to fetch your reservations');
    }
};

/**
 * Sends a request for the current user to cancel their own reservation.
 * @param {number|string} reservationId - The ID of the reservation to cancel.
 * @returns {Promise<object>} Backend response.
 */
export const cancelReservation = async (reservationId) => {
    try {
        const response = await axiosInstance.patch(`/reservations/${reservationId}/cancel`);
        return response.data;
    } catch (error) {
        console.error(`API Error cancelling reservation ${reservationId} (/reservations/.../cancel):`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to cancel reservation');
    }
};

/**
 * Fetches reservations for the calendar view for a specific month/year.
 * @param {number} year
 * @param {number} month (1-12)
 * @returns {Promise<Array>} Array of reservation objects for the calendar.
 */
export const getCalendarReservations = async (year, month) => {
    try {
        const response = await axiosInstance.get('/reservations/calendar', {
            params: { year, month }
        });
        return response.data || [];
    } catch (error) {
        console.error(`API Error fetching calendar reservations (/reservations/calendar) for ${year}-${month}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to fetch calendar reservations');
    }
};


// --- Admin Functions ---

/**
 * Fetches ALL reservations (for Admin view). Requires Admin privileges.
 * @param {object} [filters] - Optional query parameters for filtering.
 * @returns {Promise<Array>} Array of all relevant reservation objects.
 */
export const getAllReservationsForAdmin = async (filters = {}) => {
    try {
        // Calls GET /reservations (Admin protected route)
        const response = await axiosInstance.get('/reservations', { params: filters });
        return response.data || [];
    } catch (error) {
        console.error("API Error fetching all reservations for admin:", error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to fetch all reservations');
    }
};

/**
 * Updates the status (and optionally admin notes) of any reservation (Admin action).
 * @param {number|string} reservationId
 * @param {object} payload - { status: 'approved' | 'rejected', adminNotes?: string }
 * @returns {Promise<object>} Updated reservation object.
 */
export const updateReservationStatus = async (reservationId, payload) => {
    try {
        // Calls PATCH /reservations/:id/status (Admin protected route)
        const response = await axiosInstance.patch(`/reservations/${reservationId}/status`, payload);
        return response.data;
    } catch (error) {
        console.error(`API Error updating status for reservation ${reservationId}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to update reservation status');
    }
};

/**
 * Deletes any reservation by its ID (Admin action).
 * @param {number|string} reservationId
 * @returns {Promise<void>} Resolves on success (204 No Content).
 */
export const deleteReservationForAdmin = async (reservationId) => {
    try {
        // Calls DELETE /reservations/:id (Admin protected route)
        await axiosInstance.delete(`/reservations/${reservationId}`);
    } catch (error) {
        console.error(`API Error deleting reservation ${reservationId}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to delete reservation');
    }
};

/**
 * Updates specific details of a reservation.
 * @param {string|number} reservationId - The ID of the reservation to update.
 * @param {object} detailsData - Object containing the details to update (e.g., { eventName, eventDescription, additionalInfo }).
 * @returns {Promise<object>} Backend response (likely the updated reservation object and a message).
 */
export const updateReservationDetails = async (reservationId, detailsData) => {
    try {
        const response = await axiosInstance.put(`/reservations/${reservationId}/details`, detailsData);
        return response.data; // Should contain { message: '...', reservation: updatedReservation }
    } catch (error) {
        console.error(`API Error updating reservation details for ID ${reservationId}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to update reservation details.');
    }
};

// Note: getReservationById for admin might be useful later but isn't used yet by AdminReservationManagementPage

/**
 * Gets the DocuSign signing URL for a specific reservation.
 * @param {number|string} reservationId - The ID of the reservation.
 * @returns {Promise<string>} The signing URL to redirect the user to.
 */
export const getSigningUrl = async (reservationId) => {
    try {
        const response = await axiosInstance.get(`/docusign/signing-url/${reservationId}`);
        return response.data.signingUrl;
    } catch (error) {
        console.error(`API Error getting signing URL for reservation ${reservationId}:`, error.response?.data || error.message);
        throw error.response?.data || new Error('Failed to get signing URL');
    }
};